import type { TExposedPersonDeclarationWithMatchDTO } from '@doutorsim/shared/exposed/person-declarations'
import { useCallback, useMemo, useState } from 'react'
import { FIELD_CONFIG } from '../_helpers/field-config'
import { patientValueForRow } from '../_helpers/field-values'
import { toDisplayDate, toIsoSubdivisionCode } from '../_helpers/formatting'

type RowState = { checked: boolean; value: string }

interface UseConfirmationRowStateArgs {
  declarations: TExposedPersonDeclarationWithMatchDTO[]
}

// Server-authoritative seeding: deciding auto-check eligibility client-side
// would let a tampered bundle pre-check mismatching rows.
const buildInitialRows = (
  declarationsByQuestion: Map<string, TExposedPersonDeclarationWithMatchDTO>,
): Record<string, RowState> => {
  const initial: Record<string, RowState> = {}
  for (const row of FIELD_CONFIG) {
    const declaration = declarationsByQuestion.get(row.question)
    const patientValue = patientValueForRow(row, declaration)
    const checked = declaration?.shouldAutoCheck ?? false
    const initialValue =
      row.subField === 'subdivisionId'
        ? toIsoSubdivisionCode(patientValue ?? '')
        : row.key === 'birth_date'
          ? toDisplayDate(patientValue ?? '')
          : (patientValue ?? '')
    initial[row.key] = { checked, value: initialValue }
  }
  return initial
}

export const useConfirmationRowState = ({ declarations }: UseConfirmationRowStateArgs) => {
  const declarationsByQuestion = useMemo(() => {
    const map = new Map<string, TExposedPersonDeclarationWithMatchDTO>()
    for (const d of declarations) {
      map.set(d.question, d)
    }
    return map
  }, [declarations])

  const [rows, setRows] = useState<Record<string, RowState>>(() =>
    buildInitialRows(declarationsByQuestion),
  )

  const toggle = useCallback((key: string) => {
    setRows((prev) => {
      const previous = prev[key]
      if (!previous) return prev
      return { ...prev, [key]: { ...previous, checked: !previous.checked } }
    })
  }, [])

  const setValue = useCallback((key: string, value: string) => {
    setRows((prev) => {
      const previous = prev[key] ?? { checked: false, value: '' }
      return { ...prev, [key]: { ...previous, value } }
    })
  }, [])

  const checkedCount = useMemo(() => Object.values(rows).filter((r) => r.checked).length, [rows])
  const allChecked = useMemo(() => FIELD_CONFIG.every((f) => rows[f.key]?.checked), [rows])

  return {
    rows,
    declarationsByQuestion,
    toggle,
    setValue,
    allChecked,
    checkedCount,
  }
}
